/**
 * DrilldownSubTabStrip — the flat underline sub-view strip a primary drilldown
 * tab hangs its sub-views on (Documents: Uploaded / Parsed / Ocrolus / Missing;
 * Closing: Readiness / Closing Documents).
 *
 * One look for every host: a white full-width row with a single 1px baseline,
 * small icon + label buttons, the selected one in the level-1 (teal) recipe
 * with a tint→white gradient and a teal underline that replaces the baseline.
 * The Documents tab's `LoanDocumentViewToggle` renders this; a router-driven
 * host (`DrilldownRouterSubTabStrip`) derives the value from the URL.
 *
 * `options`: [{ key, label, icon?, count? }] — `count` renders "(n)"; null/
 * undefined renders no badge at all.
 */
import { useMemo } from "react";

import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import Flx from "../../../components/layout/Flx";
import { level1, teal } from "../../../theme/colors";

const DrilldownSubTabStrip = ({ options = [], value, onChange, sx }) => (
  <Flx
    sx={{
      ml: 0,
      overflowX: "auto",
      flexGrow: 0,
      // Flat underline tab strip: a white row with a single 1px gray baseline
      // the selected tab's underline sits on (no box/shadow/rounded corners).
      background: "#ffffff",
      borderBottom: `1px solid #e5e7eb`,
      width: "100%",
      ".MuiButtonBase-root": { flexShrink: 0 },
      ...sx,
    }}
  >
    <ToggleButtonGroup
      value={value}
      exclusive
      size="small"
      onChange={(_, next) => {
        if (next == null) return;
        onChange?.(next);
      }}
      sx={{
        "& .MuiToggleButton-root": {
          border: "none",
          borderRadius: 0,
          // 1px transparent underline keeps layout stable; overlaps the
          // strip's baseline so the selected underline replaces it cleanly.
          borderBottom: "1px solid transparent",
          mb: "-1px",
          background: "transparent",
          textTransform: "none",
          color: "#6B7280",
          fontWeight: 500,
          fontSize: "10px",
          lineHeight: 1.4,
          px: 1.25,
          py: 1.75,
          m: 0,
          "& svg": { color: "#6B7280" },
          "&:hover": { background: "#F7F7F5" },
        },
        "& .Mui-selected": {
          fontWeight: 700,
          color: `${level1.text} !important`,
          // Original active background — the level-1 tint → white gradient.
          background: `linear-gradient(0deg, ${level1.tint}, #ffffff) !important`,
          borderBottom: `1px solid ${level1.indicator} !important`,
          "& svg": { color: level1.text },
          "&:hover": { background: `${teal[200]} !important` },
        },
      }}
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        return (
          <ToggleButton key={opt.key} value={opt.key}>
            {Icon && <Icon className="thin" sx={{ fontSize: "13px", mr: 0.4 }} />}
            {opt.label}
            {opt.count != null && (
              <Box component="span" sx={{ ml: 0.4, color: "inherit" }}>
                ({opt.count})
              </Box>
            )}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  </Flx>
);

/**
 * The same strip driven by the router: each option carries `to`; the selected
 * option is the one whose `to` is the longest prefix of the current path, and
 * a click navigates. For tabs whose sub-views are real sub-routes (Closing).
 */
export const DrilldownRouterSubTabStrip = ({ options = [], sx }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const value = useMemo(() => {
    const pathname = location.pathname || "";
    let best = null;
    let bestLen = -1;
    options.forEach((o) => {
      if (o?.to && pathname.startsWith(o.to) && o.to.length > bestLen) {
        best = o.key;
        bestLen = o.to.length;
      }
    });
    return best;
  }, [location.pathname, options]);

  return (
    <DrilldownSubTabStrip
      options={options}
      value={value}
      onChange={(key) => {
        const to = options.find((o) => o.key === key)?.to;
        if (to) navigate(to);
      }}
      sx={sx}
    />
  );
};

export default DrilldownSubTabStrip;
